// apps/web/src/features/clients/components/ClientsSecondaryFilters.tsx
//
// Disclosed (non-search) filters for `/clients`: archive scope and the
// admin-only manager filter. Search stays in the header — see the exception
// recorded in `apps/web/docs/ui-system/component-decision-matrix.md`.
import { useTranslation } from 'react-i18next';
import { ClientArchiveFilterSchema, type ClientArchiveFilter } from '@magimp/shared';
import { Button, FormRow, Select, type SelectOption } from '../../../ui/index.js';
import { useMobileFieldStyle, useTouchActionStyle } from '../useMobileFieldStyle.js';

export interface ClientsSecondaryFiltersProps {
  archived: ClientArchiveFilter;
  onArchived: (next: ClientArchiveFilter) => void;
  managerId: string;
  onManagerId: (next: string) => void;
  /** Manager options, or `null` when the actor may not filter by manager. */
  managers: readonly { id: string; name: string }[] | null;
  onReset: () => void;
}

export function ClientsSecondaryFilters({
  archived,
  onArchived,
  managerId,
  onManagerId,
  managers,
  onReset,
}: ClientsSecondaryFiltersProps) {
  const { t } = useTranslation(['clients', 'common']);
  // The disclosed filters are tapped on the same phone as the header actions,
  // so they take the same touch minimum (AC-12).
  const fieldStyle = useMobileFieldStyle();
  const actionStyle = useTouchActionStyle();

  const archiveOptions: SelectOption[] = [
    { value: 'active', label: t('clients:filters.archive.active') },
    { value: 'archived', label: t('clients:filters.archive.archived') },
    // INVARIANT: "All" submits the wire value `all`, not an absent filter —
    // omitting the param would fall back to the schema default `active`.
    { value: 'all', label: t('clients:filters.archive.all') },
  ];
  const managerOptions: SelectOption[] = [
    { value: '', label: t('clients:filters.manager.any') },
    ...(managers ?? []).map((m) => ({ value: m.id, label: m.name })),
  ];

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 'var(--s-4)' }}>
      <div style={{ minWidth: 180 }}>
        <FormRow label={t('clients:filters.archive.label')}>
          <Select
            value={archived}
            onChange={(v) => onArchived(ClientArchiveFilterSchema.parse(v))}
            options={archiveOptions}
            style={{ ...fieldStyle, ...actionStyle }}
          />
        </FormRow>
      </div>
      {managers !== null && (
        <div style={{ minWidth: 200 }}>
          <FormRow label={t('clients:filters.manager.label')}>
            <Select
              value={managerId}
              onChange={onManagerId}
              options={managerOptions}
              style={{ ...fieldStyle, ...actionStyle }}
            />
          </FormRow>
        </div>
      )}
      <div style={{ marginBottom: 'var(--s-4)' }}>
        <Button size="sm" kind="ghost" style={actionStyle} onClick={onReset}>
          {t('clients:filters.reset')}
        </Button>
      </div>
    </div>
  );
}
